import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { centsToDollars, microsToDollars } from '../../pricing/money';
import { formatDollars, impactDisplay } from './format';

/**
 * The review screen (design spec §6.1) — the product both entry points (CSV
 * import and the manual one-cell editor) land on, and the last cheap moment
 * to catch a wrong cost before it reaches reps.
 *
 * Two things this screen exists to prevent, both load-bearing:
 *
 * 1. A PRODUCT row shows the price the PM typed (`solvedFor.targetCents`),
 *    never the solved base remainder (`value`/`previousValue`, µ$ against
 *    `CMP-BASE-<sku>`). The remainder is an implementation detail of how the
 *    price is stored — nobody can sanity-check it, and showing it invites
 *    someone to "correct" it into the wrong number. `CMP-BASE-*` never
 *    appears on screen; the product SKU stands in for it.
 * 2. The footer states, in words, that nothing is live until publish, with a
 *    link to get there. A PM who commits and sees rep prices unchanged will
 *    otherwise reasonably conclude the save failed and try again.
 *
 * `review` is `null` until something is staged (`useCatalogDraft.js:137`) —
 * this component must render a quiet placeholder rather than throw.
 */

const REASSERT_TITLE =
  'Re-asserted: this price was recorded again unchanged, not a new price.';

/** §7.2's table, verbatim, minus near-miss (handled separately below because
 *  it carries its own "did you mean" suggestion rather than fixed copy). */
const UNMATCHED_REASON_TEXT = {
  unknown: 'No component with this name exists yet — create it, then compose it.',
  uncomposed: 'This cost is known but used in no composition — add it to a composition first.',
  ambiguous: 'This name matches more than one component — pick which one it means.',
};

/** Every `Blocked.reason` value (`costDraft.ts:120`) carries a human-written
 *  `detail` already tailored to that reason by `planWrites`/`backDerive` — so
 *  rendering `detail` unconditionally, with no branch on `reason`, is what
 *  makes all four cases legible instead of three. `reason` itself only
 *  labels the `title` tooltip below. */
const BLOCKED_REASON_LABEL = {
  infeasible: 'Cannot price',
  'negative-remainder': 'Cannot price',
  'malformed-amount': 'Unreadable amount',
  'duplicate-tuple': 'Duplicate target',
  'not-a-cost-kind': 'Not a cost row',
};

/** What to show in the "Change" column, and the label standing in for the
 *  row's real component SKU. Isolated here so the product-row rule (show the
 *  typed price, never the remainder) has exactly one place it can be gotten
 *  wrong. */
function plannedDisplay(row) {
  if (row.solvedFor) {
    return {
      label: row.solvedFor.productSku,
      change: formatDollars(centsToDollars(row.solvedFor.targetCents)),
    };
  }
  const next = formatDollars(microsToDollars(row.value));
  const prev = row.previousValue === null ? null : formatDollars(microsToDollars(row.previousValue));
  return {
    label: row.componentSku,
    change: prev !== null ? `${prev} → ${next}` : next,
  };
}

function UnmatchedRow({ item, componentSkus, busy, onResolve }) {
  const { cell, reason, suggestion } = item;
  const [selected, setSelected] = useState('');

  return (
    <div className="mb-3 text-sm border-t border-gray-800 pt-2">
      <p>
        {cell.ref} <span className="text-gray-500 text-xs">({cell.quarter})</span>
      </p>
      {reason === 'near-miss' && suggestion ? (
        <p className="text-gray-400 text-xs">Did you mean {suggestion}?</p>
      ) : (
        <p className="text-gray-400 text-xs">
          {UNMATCHED_REASON_TEXT[reason] ?? 'This reference could not be matched to a component.'}
        </p>
      )}
      <div className="flex items-center gap-2 mt-1">
        <select
          aria-label={cell.ref}
          disabled={busy}
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="bg-gray-900 border border-gray-700 rounded px-1 py-0.5"
        >
          <option value="">Select component…</option>
          {componentSkus.map((sku) => (
            <option key={sku} value={sku}>{sku}</option>
          ))}
        </select>
        <button
          type="button"
          disabled={busy || !selected}
          onClick={() => onResolve({ ref: cell.ref, componentSku: selected })}
          className="px-2 py-0.5 rounded border border-gray-600 hover:bg-gray-800 disabled:opacity-50"
        >
          Resolve
        </button>
      </div>
    </div>
  );
}

/** The planned rows, grouped by the SKU the screen shows for them (the product
 *  SKU for a solved product row, the component SKU otherwise), in first-seen
 *  order. The cost worksheet (CONF-209) plans one row per (SKU, quarter) from
 *  the current quarter to the horizon, so a single drive is a dozen
 *  near-identical lines; folding them behind the SKU is what lets the PM move
 *  on to the next one. Grouping is by label, not adjacency, so a SKU whose
 *  rows the sheet happened to interleave still folds as one. */
function groupPlanned(planned) {
  const groups = new Map();
  for (const row of planned) {
    const { label, change } = plannedDisplay(row);
    if (!groups.has(label)) groups.set(label, { label, rows: [] });
    groups.get(label).rows.push({ row, change });
  }
  return [...groups.values()];
}

export function ReviewPanel({
  review, componentSkus = [], onCommit, onResolveAlias, onReset, busy = false, error = null,
}) {
  // Folded SKU labels. Cleared whenever a different review arrives: a fresh
  // upload (or a re-match after an alias resolve) may have changed what is
  // under a label, and a row hidden by a fold the PM made against the
  // PREVIOUS review is a row they never looked at.
  const [collapsed, setCollapsed] = useState(() => new Set());
  useEffect(() => { setCollapsed(new Set()); }, [review]);

  const groups = useMemo(() => groupPlanned(review?.planned ?? []), [review]);

  if (!review) {
    return (
      <div className="text-gray-500 text-sm">
        Nothing to review yet — upload a sheet or edit a cell to get started.
      </div>
    );
  }

  const {
    planned = [], noOps = [], unmatched = [], blocked = [], impact = [], impactError = null,
  } = review;

  const commitDisabled = busy || Boolean(impactError) || planned.length === 0;

  const handleCommit = () => {
    if (commitDisabled) return;
    onCommit();
  };

  const handleResolve = (input) => {
    if (busy) return;
    onResolveAlias(input);
  };

  const handleReset = () => {
    if (busy || !onReset) return;
    onReset();
  };

  const toggleFold = (label) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(label)) next.delete(label); else next.add(label);
    return next;
  });

  return (
    <div>
      {error && (
        <div className="text-red-400 mb-3" role="alert">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="font-bold text-sm text-gray-400 mb-2">What will be written</h2>
          <table className="w-full text-sm" aria-label="Planned changes">
            <thead className="text-gray-400 text-left">
              <tr><th>Component</th><th>Quarter</th><th>Change</th></tr>
            </thead>
            <tbody>
              {groups.map(({ label, rows }) => {
                const foldable = rows.length > 1;
                const open = !collapsed.has(label);
                // The caret sits IN the first data row rather than on a
                // header row of its own: every row of this table is a planned
                // write, and a header row carrying the SKU would be found
                // first by anything (tests included) looking the SKU up by
                // row. Collapsed, that same first row becomes the summary.
                const caret = foldable && (
                  <button
                    type="button"
                    onClick={() => toggleFold(label)}
                    aria-expanded={open}
                    aria-label={`${open ? 'Collapse' : 'Expand'} ${label}`}
                    className="text-orange-400 mr-1"
                  >
                    {open ? '▾' : '▸'}
                  </button>
                );
                if (!open) {
                  const first = rows[0].row.effectiveFrom;
                  const last = rows[rows.length - 1].row.effectiveFrom;
                  return (
                    <tr key={label} className="border-t border-gray-800 text-gray-400">
                      <td>{caret}{label}</td>
                      <td>{first} – {last}</td>
                      <td>{rows.length} quarters hidden</td>
                    </tr>
                  );
                }
                return rows.map(({ row, change }, i) => (
                  <tr
                    key={`${row.componentSku}#${row.effectiveFrom}`}
                    className="border-t border-gray-800"
                    title={row.reassertion ? REASSERT_TITLE : undefined}
                  >
                    <td className={i > 0 ? 'text-gray-500' : undefined}>{i === 0 && caret}{label}</td>
                    <td>{row.effectiveFrom}</td>
                    <td>{change}</td>
                  </tr>
                ));
              })}
            </tbody>
          </table>

          {noOps.length > 0 && (
            <p className="text-gray-500 text-xs mt-2">{noOps.length} cells unchanged</p>
          )}

          {blocked.length > 0 && (
            <div className="mt-4">
              <h2 className="font-bold text-sm text-red-400 mb-2">Blocked — not written</h2>
              <ul className="text-sm">
                {blocked.map((b) => (
                  <li
                    key={`${b.cell.ref}#${b.cell.quarter}`}
                    className="text-red-400 mb-1"
                    title={BLOCKED_REASON_LABEL[b.reason] ?? b.reason}
                  >
                    {b.detail}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {unmatched.length > 0 && (
            <div className="mt-4">
              <h2 className="font-bold text-sm text-gray-400 mb-2">Unmatched</h2>
              {unmatched.map((item) => (
                <UnmatchedRow
                  key={`${item.cell.ref}#${item.cell.quarter}`}
                  item={item}
                  componentSkus={componentSkus}
                  busy={busy}
                  onResolve={handleResolve}
                />
              ))}
            </div>
          )}
        </div>

        <div>
          <h2 className="font-bold text-sm text-gray-400 mb-2">What it costs</h2>
          <table className="w-full text-sm" aria-label="Price movement">
            <thead className="text-gray-400 text-left">
              <tr>
                <th>SKU</th><th>Quarter</th><th>What</th>
                <th>Before</th><th>After</th><th>Δ</th>
              </tr>
            </thead>
            <tbody>
              {impact.map((row) => {
                const shown = impactDisplay(row);
                return (
                  // `kind` belongs in the key: a single (sku, quarter) can now
                  // move BOTH its price and its discount, and the old
                  // sku#quarter key would collide on the two rows and drop one
                  // — silently hiding half of exactly the movement this table
                  // was extended to show.
                  <tr
                    key={`${row.productSku}#${row.quarter}#${row.kind ?? 'price'}`}
                    className="border-t border-gray-800"
                  >
                    <td>{row.productSku}</td>
                    <td>{row.quarter}</td>
                    <td>{shown.what}</td>
                    <td>{shown.before}</td>
                    <td>{shown.after}</td>
                    <td>{shown.delta}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {impactError && (
            <div id="review-impact-error" className="text-red-400 mt-2 text-sm">
              {impactError}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 border-t border-gray-800 pt-3">
        <p className="text-sm text-gray-400 mb-2">
          These costs are not live until published.{' '}
          <Link to="/admin/prices" className="underline text-orange-400">Go to price editions</Link>
          {' '}to publish them.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={commitDisabled}
            onClick={handleCommit}
            // Ties the disabled reason (rendered next to the impact table, on
            // the other side of the two-column layout) to this control — a
            // dead button with no explanation is its own failure, and that
            // explanation has to be reachable FROM the button, not merely
            // present somewhere on the page.
            aria-describedby={impactError ? 'review-impact-error' : undefined}
            className="bg-orange-600 disabled:opacity-50 px-3 py-1 rounded"
          >
            Commit {planned.length} {planned.length === 1 ? 'row' : 'rows'}
          </button>
          {/* Optional: only a host that can actually drop the staged draft
              (CatalogPage, via useCatalogDraft.discard) offers it. Disabled
              while a write is in flight for the same reason Commit is. */}
          {onReset && (
            <button
              type="button"
              disabled={busy}
              onClick={handleReset}
              className="border border-gray-600 hover:bg-gray-800 disabled:opacity-50 px-3 py-1 rounded"
            >
              Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReviewPanel;
